import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Settings, Send, Tag, FolderTree, Menu, Pin } from 'lucide-react';

export default function BotSettings({ selectedTeam }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [canManage, setCanManage] = useState(false);
  const [formData, setFormData] = useState({
    send_time: '09:30',
    brands_cols: 2,
    categories_cols: 2,
    timezone: 'Europe/Istanbul',
    auto_send_menu: false,
    menu_show_buttons: true,
    menu_show_brands: true,
    menu_show_categories: true,
    pin_menu_messages: false
  });

  useEffect(() => {
    if (selectedTeam) {
      loadSettings();
      checkPermissions();
    }
  }, [selectedTeam]);

  const checkPermissions = async () => {
    try {
      const res = await axiosInstance.get(`/teams/${selectedTeam}/role`);
      setCanManage(res.data.can_manage_settings);
    } catch (err) {
      console.error('Failed to check permissions', err);
    }
  };

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/teams/${selectedTeam}/settings`);
      setSettings(res.data);
      setFormData({
        send_time: res.data.send_time,
        brands_cols: res.data.brands_cols,
        categories_cols: res.data.categories_cols,
        timezone: res.data.timezone,
        auto_send_menu: res.data.auto_send_menu || false,
        menu_show_buttons: res.data.menu_show_buttons !== false,
        menu_show_brands: res.data.menu_show_brands !== false,
        menu_show_categories: res.data.menu_show_categories !== false,
        pin_menu_messages: res.data.pin_menu_messages || false
      });
    } catch (err) {
      toast.error('Ayarlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axiosInstance.put(`/teams/${selectedTeam}/settings`, formData);
      toast.success('Ayarlar kaydedildi');
      loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kaydetme başarısız');
    } finally {
      setSaving(false);
    }
  };

  if (!selectedTeam) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <Card className="w-full max-w-md text-center overflow-hidden">
          <CardContent className="pt-8 pb-8 sm:pt-12 sm:pb-12">
            <Settings className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-purple-500" />
            <h2 className="text-xl sm:text-2xl font-bold mb-2">Mağaza Seçin</h2>
            <p className="text-sm sm:text-base text-gray-600">Bot ayarlarını yönetmek için bir mağaza seçin.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="bot-settings-page">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Bot Ayarları</h1>
        <p className="text-sm sm:text-base text-gray-600">Telegram bot ayarlarınızı yönetin</p>
      </div>

      <div className="max-w-4xl">
        <Card className="shadow-lg border-0 overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
            <CardTitle className="text-lg sm:text-xl flex items-center">
              <Settings className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
              Genel Ayarlar
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {!canManage && (
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-6 rounded-r-xl">
                <p className="text-sm text-yellow-700">
                  ⚠️ Sadece mağaza sahibi veya admin bu ayarları değiştirebilir.
                </p>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div className="space-y-2">
                  <Label htmlFor="send_time" className="text-sm sm:text-base">Menü Gönderim Saati</Label>
                  <Input
                    id="send_time"
                    type="time"
                    value={formData.send_time}
                    onChange={(e) => setFormData({ ...formData, send_time: e.target.value })}
                    data-testid="send-time-input"
                    className="text-sm sm:text-base"
                    disabled={!canManage}
                  />
                  <p className="text-xs sm:text-sm text-gray-500">Her gün menünün otomatik gönderileceği saat</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="timezone" className="text-sm sm:text-base">Saat Dilimi</Label>
                  <select
                    id="timezone"
                    value={formData.timezone}
                    onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                    className="flex h-10 w-full rounded-2xl border border-input bg-background px-3 py-2 text-sm sm:text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="timezone-select"
                    disabled={!canManage}
                  >
                    <option value="Europe/Istanbul">Europe/Istanbul (GMT+3)</option>
                    <option value="Europe/London">Europe/London (GMT+0)</option>
                    <option value="America/New_York">America/New_York (GMT-5)</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="brands_cols" className="text-sm sm:text-base">Marka Sütun Sayısı</Label>
                  <Input
                    id="brands_cols"
                    type="number"
                    min="1"
                    max="4"
                    value={formData.brands_cols}
                    onChange={(e) => setFormData({ ...formData, brands_cols: parseInt(e.target.value) })}
                    data-testid="brands-cols-input"
                    className="text-sm sm:text-base"
                    disabled={!canManage}
                  />
                  <p className="text-xs sm:text-sm text-gray-500">Markalar kaç sütunda gösterilecek (1-4)</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="categories_cols" className="text-sm sm:text-base">Kategori Sütun Sayısı</Label>
                  <Input
                    id="categories_cols"
                    type="number"
                    min="1"
                    max="4"
                    value={formData.categories_cols}
                    onChange={(e) => setFormData({ ...formData, categories_cols: parseInt(e.target.value) })}
                    data-testid="categories-cols-input"
                    className="text-sm sm:text-base"
                    disabled={!canManage}
                  />
                  <p className="text-xs sm:text-sm text-gray-500">Kategoriler kaç sütunda gösterilecek (1-4)</p>
                </div>
              </div>

              {/* Otomatik Menü Gönderimi */}
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-2xl border-2 border-blue-200">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <Label htmlFor="auto_send_menu" className="text-sm sm:text-base font-semibold text-gray-900 cursor-pointer">
                      Otomatik Menü Gönderimi
                    </Label>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">
                      Aktif edildiğinde menü her gün belirlenen saatte otomatik gönderilir
                    </p>
                  </div>
                  <label className={`relative inline-flex items-center ml-4 ${!canManage ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                    <input
                      id="auto_send_menu"
                      type="checkbox"
                      checked={formData.auto_send_menu}
                      onChange={(e) => setFormData({ ...formData, auto_send_menu: e.target.checked })}
                      className="sr-only peer"
                      disabled={!canManage}
                    />
                    <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-purple-500 peer-checked:to-pink-500 peer-disabled:opacity-50"></div>
                  </label>
                </div>
              </div>

              {/* Menü Bölümleri Ayarları */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-2xl border-2 border-green-200">
                <div className="mb-4">
                  <h3 className="text-sm sm:text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Send className="w-4 h-4" />
                    Menü Gönderim Bölümleri
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">
                    Menü gönderilirken hangi bölümlerin dahil edileceğini seçin
                  </p>
                </div>
                
                <div className="space-y-3">
                  {/* Menü Butonları */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-xl border">
                    <div className="flex items-center gap-3">
                      <Menu className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="font-medium text-sm">Menü Butonları</p>
                        <p className="text-xs text-gray-500">Özel linkler ve butonlar</p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.menu_show_buttons}
                      onCheckedChange={(checked) => setFormData({ ...formData, menu_show_buttons: checked })}
                      disabled={!canManage}
                    />
                  </div>

                  {/* Markalar */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-xl border">
                    <div className="flex items-center gap-3">
                      <Tag className="w-5 h-5 text-purple-500" />
                      <div>
                        <p className="font-medium text-sm">Markalar</p>
                        <p className="text-xs text-gray-500">Marka listesi</p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.menu_show_brands}
                      onCheckedChange={(checked) => setFormData({ ...formData, menu_show_brands: checked })}
                      disabled={!canManage}
                    />
                  </div>

                  {/* Kategoriler */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-xl border">
                    <div className="flex items-center gap-3">
                      <FolderTree className="w-5 h-5 text-green-500" />
                      <div>
                        <p className="font-medium text-sm">Kategoriler</p>
                        <p className="text-xs text-gray-500">Kategori listesi</p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.menu_show_categories}
                      onCheckedChange={(checked) => setFormData({ ...formData, menu_show_categories: checked })}
                      disabled={!canManage}
                    />
                  </div>
                </div>
              </div>

              {/* Menü Sabitleme Ayarı */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-2xl border-2 border-amber-200">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Pin className="w-4 h-4 text-amber-600" />
                      <Label htmlFor="pin_menu_messages" className="text-sm sm:text-base font-semibold text-gray-900 cursor-pointer">
                        Menüyü Sabitle
                      </Label>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">
                      Her yeni menü gönderildiğinde otomatik olarak sabitlenir ve önceki sabitlenmiş menü kaldırılır
                    </p>
                  </div>
                  <Switch
                    id="pin_menu_messages"
                    checked={formData.pin_menu_messages}
                    onCheckedChange={(checked) => setFormData({ ...formData, pin_menu_messages: checked })}
                    disabled={!canManage}
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                disabled={saving || !canManage}
                className="w-full sm:w-auto bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="save-settings-button"
              >
                {saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0 mt-6 overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white">
            <CardTitle className="text-lg sm:text-xl">Mevcut Ayarlar</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-purple-50 p-3 sm:p-4 rounded-2xl">
                <p className="text-xs sm:text-sm text-gray-600 mb-1">Gönderim Saati</p>
                <p className="text-base sm:text-lg font-semibold text-purple-700">{settings?.send_time}</p>
              </div>
              <div className="bg-purple-50 p-3 sm:p-4 rounded-2xl">
                <p className="text-xs sm:text-sm text-gray-600 mb-1">Saat Dilimi</p>
                <p className="text-base sm:text-lg font-semibold text-purple-700">{settings?.timezone}</p>
              </div>
              <div className="bg-purple-50 p-3 sm:p-4 rounded-2xl">
                <p className="text-xs sm:text-sm text-gray-600 mb-1">Marka Sütun</p>
                <p className="text-base sm:text-lg font-semibold text-purple-700">{settings?.brands_cols}</p>
              </div>
              <div className="bg-purple-50 p-3 sm:p-4 rounded-2xl">
                <p className="text-xs sm:text-sm text-gray-600 mb-1">Kategori Sütun</p>
                <p className="text-base sm:text-lg font-semibold text-purple-700">{settings?.categories_cols}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
